import * as React from 'react';
import {
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  useColorScheme,
} from '@mui/material';
import Grid from '@mui/material/Grid2';

import { ReactComponent as CloseIcon } from '../../assets/icons/info/closeCircle.svg';
import { ReactComponent as CheckedIcon } from '../../assets/icons/info/checkInCircle.svg';
import { GlassColors } from '../../components/glassmorphism/GlassmorphismStyles';

type CompareRow = {
  oldTitle: string;
  oldText: string;
  womTitle: string;
  womText: string;
};

const rows: CompareRow[] = [
  {
    oldTitle: 'You pay just to say "Hello"',
    oldText:
      'Platforms charge you £5-£20 upfront just to send a quote or message a client, even if you never get the job.',
    womTitle: '100% Free to quote',
    womText:
      'You never pay to talk to a client. We automatically deduct a transparent 10% fee only after the job is successfully completed.',
  },
  {
    oldTitle: '"Pay-to-win" competition',
    oldText:
      'Your lead is sold to 5-10 other pros. Platforms often let people pay extra money to "boost" their profile and hide yours.',
    womTitle: 'No hidden advantages',
    womText:
      'Applying is free for everyone. You win jobs based on your actual skills, portfolio, and reviews—not your marketing budget.',
  },
  {
    oldTitle: 'Subscriptions & Credit Packs',
    oldText:
      'You are forced to pay expensive monthly fees or buy "credits" in bulk before you even find any work.',
    womTitle: 'Zero upfront costs',
    womText:
      'No monthly subscriptions, no hidden charges, and no credits to buy. You join for free and stay for free.',
  },
  {
    oldTitle: 'They control your prices',
    oldText:
      'The app dictates your hourly rates, forces a "race to the bottom," or adds massive markup fees to the client’s bill.',
    womTitle: 'You are the boss',
    womText:
      'You set your own rates (fixed or hourly). You negotiate directly with the client. You keep control of your business.',
  },
];

function SectionHeader({
  icon,
  label,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  color: string;
  borderRadius: string;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
      }}
    >
      {icon}
      <Typography
        sx={{
          fontFamily: 'Roboto, sans-serif',
          fontWeight: 700,
          color,
          fontSize: 20,
          lineHeight: '175%',
          letterSpacing: '.15px',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function TextBlock({
  title,
  text,
  titleColor,
  textColor,
}: {
  title: string;
  text: string;
  titleColor: string;
  textColor: string;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography
        sx={{
          fontFamily: 'Roboto, sans-serif',
          fontWeight: 700,
          lineHeight: '175%',
          letterSpacing: '.15px',
          color: titleColor,
          fontSize: 16,
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          fontFamily: 'Roboto, sans-serif',
          color: textColor,
          fontSize: 16,
          lineHeight: 1.5,
          letterSpacing: '.15px',
        }}
      >
        {text}
      </Typography>
    </Box>
  );
}

export default function CompareBlock() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('tablet'));
  const isDark = theme.palette.mode === 'dark';

  const cardSx = {
    borderRadius: '32px',
    overflow: 'hidden',
    boxShadow: `
        0px 0px 34.77px 0px rgba(0, 0, 0, 0.05) inset,
        0px -0.5px 0.25px 0px rgba(0, 0, 0, 0.5) inset,
        0px 0.5px 0.75px 0px rgba(255, 255, 255, 1) inset,
        0px 2.07px 3.73px 0px rgba(0, 0, 0, 0.3),
        0px 2.08px 2.48px -2px rgba(0, 0, 0, 0.4),
        0px 3.08px 2px -2px rgba(0, 0, 0, 0.33)`,
  } as const;

  const innerPanelSx = (tone: 'old' | 'wom') =>
    ({
      backgroundColor:
        tone === 'old'
          ? isDark
            ? '#393939'
            : '#D1D0C6'
          : isDark
            ? '#1A1A1A'
            : '#E5E5DC',
      boxShadow: `
        0px 0px 34.77px 0px rgba(0, 0, 0, 0.05) inset,
        0px -0.5px 0.25px 0px rgba(0, 0, 0, 0.5) inset,
        0px 0.5px 0.75px 0px rgba(255, 255, 255, 1) inset,
        0px 2.07px 3.73px 0px rgba(0, 0, 0, 0.3),
        0px 2.08px 2.48px -2px rgba(0, 0, 0, 0.4),
        0px 3.08px 2px -2px rgba(0, 0, 0, 0.33)`,
    }) as const;

  const oldColor = isDark
    ? GlassColors.dark.textSecondary
    : GlassColors.light.textSecondary;
  const womColor = isDark ? GlassColors.accent : GlassColors.secondary;
  const oldTextColor = isDark
    ? GlassColors.dark.textSecondary
    : GlassColors.light.textSecondary;
  const womTextColor = isDark
    ? GlassColors.dark.textPrimary
    : GlassColors.light.textPrimary;

  return (
    <>
      {/* ===== MOBILE: stacked cards ===== */}
      {isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {rows.flatMap((r, idx) => [
            <Box key={`old-${idx}`} sx={{ ...cardSx, mb: '8px' }}>
              <Box
                sx={{
                  px: '24px',
                  py: '8px',
                  ...innerPanelSx('old'),
                  '& svg': {
                    width: '24px',
                    height: '24px',
                  },
                }}
              >
                <SectionHeader
                  icon={<CloseIcon />}
                  label='The Old Way'
                  color={oldColor}
                  borderRadius='32px 32px 0 0'
                />
              </Box>
              <Box sx={{ px: '24px', py: '8px', ...innerPanelSx('old') }}>
                <TextBlock
                  title={r.oldTitle}
                  text={r.oldText}
                  titleColor={oldColor}
                  textColor={oldTextColor}
                />
              </Box>
            </Box>,

            <Box
              key={`wom-${idx}`}
              sx={{ ...cardSx, '&:not(:last-child)': { mb: '24px' } }}
            >
              <Box
                sx={{
                  px: '24px',
                  py: '8px',
                  ...innerPanelSx('wom'),
                  '& svg': {
                    width: '24px',
                    height: '24px',
                  },
                }}
              >
                <SectionHeader
                  icon={<CheckedIcon />}
                  label='The WOM Way'
                  color={womColor}
                  borderRadius='32px 32px 0 0'
                />
              </Box>
              <Box sx={{ px: '24px', py: '8px', ...innerPanelSx('wom') }}>
                <TextBlock
                  title={r.womTitle}
                  text={r.womText}
                  titleColor={womColor}
                  textColor={womTextColor}
                />
              </Box>
            </Box>,
          ])}
        </Box>
      ) : (
        /* ===== DESKTOP: table-like compare card ===== */
        <Box>
          {/* header row */}
          <Grid container>
            <Grid
              size={6}
              sx={{
                px: '24px',
                py: '8px',
                '& svg': {
                  width: '24px',
                  height: '24px',
                },
                ...innerPanelSx('old'),
                borderRadius: '32px 0 0 0',
              }}
            >
              <SectionHeader
                icon={<CloseIcon />}
                label='The Old Way'
                color={oldColor}
                borderRadius='32px 0 0 0'
              />
            </Grid>
            <Grid
              size={6}
              sx={{
                px: '24px',
                py: '8px',
                '& svg': {
                  width: '24px',
                  height: '24px',
                },
                ...innerPanelSx('wom'),
                borderRadius: '0 32px 0 0',
              }}
            >
              <SectionHeader
                icon={<CheckedIcon />}
                label='The WOM Way'
                color={womColor}
                borderRadius='0 32px 0 0'
              />
            </Grid>
          </Grid>

          {/* rows */}
          {rows.map((r, idx) => {
            const isLast = idx === rows.length - 1;

            return (
              <React.Fragment key={idx}>
                <Grid container>
                  <Grid
                    size={6}
                    sx={{
                      p: '8px 24px 16px',
                      ...innerPanelSx('old'),
                      borderRadius: isLast ? '0 0 0 32px' : '',
                    }}
                  >
                    <TextBlock
                      title={r.oldTitle}
                      text={r.oldText}
                      titleColor={oldColor}
                      textColor={oldTextColor}
                    />
                  </Grid>
                  <Grid
                    size={6}
                    sx={{
                      p: '8px 24px 16px',
                      ...innerPanelSx('wom'),
                      borderRadius: isLast ? '0 0 32px 0' : '',
                    }}
                  >
                    <TextBlock
                      title={r.womTitle}
                      text={r.womText}
                      titleColor={womColor}
                      textColor={womTextColor}
                    />
                  </Grid>
                </Grid>
              </React.Fragment>
            );
          })}
        </Box>
      )}
    </>
  );
}
